import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "socket.io-client";

//import { toast } from "react-toastify";

//import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { guardarBaseErpDoUsuario, guardarBaseErpDoDeepLink } from "../../helpers/urlRetornoCrm";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	api.interceptors.request.use(
		config => {
			const token = localStorage.getItem("token");
			if (token) {
				config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
				setIsAuth(true);
			}
			return config;
		},
		error => {
			Promise.reject(error);
		}
	);

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;
			const body = error?.response?.data;
			const isInactivePayload =
				body && (body.error === "ERR_USER_INACTIVE" ||
					body.message === "ERR_USER_INACTIVE" ||
					body.inactive === true);
			const isOutOfSchedulePayload =
				body && (body.error === "ERR_USER_OUT_OF_SCHEDULE" ||
					body.message === "ERR_USER_OUT_OF_SCHEDULE" ||
					body.outOfSchedule === true);

			// Usuário desativado: NÃO faz refresh, NÃO derruba sessão, NÃO limpa token.
			// Atualiza estado e o AppGate redireciona para /blocked-user.
			if (isInactivePayload) {
				setUser(prev => (prev && prev.ativo === false ? prev : { ...(prev || {}), ativo: false }));
				return Promise.reject(error);
			}

			// Fora do horário de acesso: mesmo tratamento do desativado. O token é preservado de
			// propósito — o horário volta sozinho, e derrubar a sessão obrigaria o usuário a passar
			// pelo ERP de novo sem motivo.
			if (isOutOfSchedulePayload) {
				setUser(prev => (prev && prev.foraHorarioAcesso ? prev : { ...(prev || {}), foraHorarioAcesso: true }));
				return Promise.reject(error);
			}

			if (error?.response?.status === 403 && !originalRequest._retry) {
				originalRequest._retry = true;

				const { data } = await api.post("/auth/refresh_token");
				if (data) {
					localStorage.setItem("token", JSON.stringify(data.token));
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
				}
				return api(originalRequest);
			}
			if (error?.response?.status === 401) {
				localStorage.removeItem("token");
				api.defaults.headers.Authorization = undefined;
				setIsAuth(false);
			}
			return Promise.reject(error);
		}
	);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
					// Sessão que não veio de deep link (favorito, aba reaberta) não tem a raiz do
					// ERP no localStorage — sem isso os atalhos de voltar pro ERP ficam mortos.
					guardarBaseErpDoUsuario(data.user);
				} catch (err) {
					//toastError(err);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		// Bloqueios ANTES da inicialização realtime:
		if (loading) return undefined;
		if (!user || JSON.stringify(user) === '{}') return undefined;
		if (user.ativo === false) return undefined;
		if (user.foraHorarioAcesso) return undefined;

		const normalizeUrl = u => (u || "").trim().replace(/\/+$/, "");
		const urlsUnicas = new Set([normalizeUrl(process.env.REACT_APP_BACKEND_URL)]);
		(user.whatsapps || [])
			.map(w => normalizeUrl(w.urlBackend))
			.filter(Boolean)
			.forEach(url => urlsUnicas.add(url));

		const sockets = [];
		[...urlsUnicas].forEach(url => {
			const socket = openSocket(url.trim() + "/");
			sockets.push(socket);

			socket.on("user", data => {
				if (data.action === "update" && data.user.id === user.id) {
					setUser(data.user);
				}
			});
		});
		return () => {
			sockets.forEach(socket => socket.disconnect());
		};
	}, [user, loading]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			setIsAuth(false);
			setUser({});
			api.defaults.headers.Authorization = undefined;
			// `url_retorno_crm` NÃO é apagado aqui: quem o substitui é o login logo abaixo (o
			// valor deste usuário, e depois o retorno do deep link). Apagar antes deixava a
			// sessão sem a raiz do ERP sempre que o login falhava no meio ou o link vinha sem
			// essa parte — a URL do lead virava "null/app/..." e a tela ficava em branco
			// (#14631). Sem base nova, a antiga serve.
			localStorage.removeItem("icode_options");
			localStorage.removeItem("filialId");
			localStorage.removeItem("paramId");
			localStorage.removeItem("funilId");
		} catch (err) {
			console.log("Erro ao fazer logout " + err);
		}
		try {
			await new Promise(r => setTimeout(r, 100));

			const { data } = await api.post("/auth/login", userData);

			// 1) ESTADO DE AUTENTICAÇÃO PRIMEIRO.
			// Qualquer parsing do deep link (tela/id) vem DEPOIS e protegido. Antes,
			// um `tela` sem "@" estourava em split("@")[1].replaceAll(...) ANTES de
			// setIsAuth/setUser: o backend autenticava (token salvo) mas o front
			// ficava sem estado de auth → /tickets nunca montava e a tela de login
			// remontava em loop (só o F5, que entra pelo refresh_token, resolvia).
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			// Base do ERP que o backend guardou DESTE usuário, por cima do que sobrou de quem
			// usou a máquina antes. Vem antes do parsing do deep link de propósito: o retorno
			// DESTA navegação (item 3) sobrescreve logo abaixo, e é ele que traz o domínio
			// exato desta entrada.
			guardarBaseErpDoUsuario(data.user, { substituir: true });
			//toast.success(i18n.t("auth.toasts.success"));

			// 2) Usuário desativado → /blocked-user.
			if (data.user && data.user.ativo === false) {
				setLoading(false);
				history.push("/blocked-user");
				return;
			}

			// 3) Deep link do CRM em `tela` = "<rota>@<retorno>@<opcoes>" (partes
			// opcionais). Parsing tolerante: faltando qualquer parte, segue sem
			// quebrar o login. Destino é a rota do deep link (ou /tickets).
			let destino = "/tickets";
			try {
				const telaPartes = (userData?.tela || "").split("@");
				const telaPath = telaPartes[0];
				guardarBaseErpDoDeepLink(telaPartes[1]);
				if (telaPartes[2]) {
					localStorage.setItem("icode_options", telaPartes[2]);
				}
				// telaPath vem como "tickets"/"chat"/etc. (sem barra) no deep link
				// do CRM. Normaliza para "/tickets"/"/chat" — antes só aceitava
				// telaPath já com "/", então o alvo era ignorado e caía no default.
				if (telaPath) {
					destino = telaPath.startsWith("/") ? telaPath : "/" + telaPath;
				}
				if (userData.id && userData.id !== "undefined") {
					const idParts = String(userData.id).split("@");
					localStorage.setItem("filialId", idParts[0]);
					localStorage.setItem("paramId", idParts[1]);
					localStorage.setItem("funilId", idParts[2]);
					if (idParts[1]) destino += "/" + idParts[1];
				}
			} catch (e) {
				destino = "/tickets";
			}

			// Navega ANTES de limpar o loading. Assim não existe um render
			// intermediário em "/login" com isAuth=true (que dispararia o
			// Redirect hardcoded para "/tickets" no Route.js e descartaria o
			// ticket/funil do deep link). Quando o loading cai, a URL já é o
			// destino correto (ex.: /tickets/2549).
			history.push(destino);
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	// Sair tem de funcionar mesmo com o acesso bloqueado: a limpeza do estado local ficava depois
	// do `await`, e nas telas de bloqueio a chamada volta 403 (`isAuth`) — o clique não fazia nada,
	// nem mostrava toast (o código tem tela própria). Agora sai no `finally`, e a navegação é
	// obrigatória: /blocked-user e /fora-horario-acesso são rotas públicas do AppGate e
	// continuariam renderizando com a sessão já limpa.
	const handleLogout = async () => {
		setLoading(true);
		try {
			await api.delete("/auth/logout");
		} catch (err) {
			// Sem tratamento: no pior caso o cookie de refresh fica para trás, a sessão local sai igual.
		} finally {
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/sessao-encerrada");
		}
	};

	// Reavalia o acesso numa requisição autenticada — quem responde é o `isAuth`. O
	// `refresh_token` do boot não passa por ele, então recarregar a página não reavaliava nada e a
	// tela de bloqueio virava beco sem saída mesmo depois do horário voltar. Devolve as janelas de
	// hoje para a tela poder dizer qual é o horário.
	const revalidarAcesso = async () => {
		try {
			await api.get("/auth/situacao");
			setUser(prev => (prev && prev.foraHorarioAcesso ? { ...prev, foraHorarioAcesso: false } : prev));
			return { liberado: true, janelasHoje: [] };
		} catch (err) {
			return { liberado: false, janelasHoje: err?.response?.data?.janelasHoje || [] };
		}
	};

	// O status online é o único campo que muda fora do socket: o botão aplica o estado que
	// o ERP devolveu na própria resposta, porque esperar a volta pelo socket era o que fazia
	// o clique parecer não ter feito nada quando ela não chegava.
	const aplicarStatusOnline = onLine => {
		setUser(usuarioAtual => ({ ...usuarioAtual, onLine }));
	};

	return { isAuth, user, loading, handleLogin, handleLogout, revalidarAcesso, aplicarStatusOnline };
};

export default useAuth;
